import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
interface SignInProps {
  initialEmail?: string;
  onSignIn: (email: string, isFirstTime: boolean) => void;
}
export function SignIn({
  initialEmail = '',
  onSignIn
}: SignInProps) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const canSubmit = email.trim().length > 3 && password.length >= 1;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSignIn(email.trim(), isFirstTime);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} data-id="element-488">
      <div className="text-center mb-8" data-id="element-489">
        <h1 className="text-3xl md:text-4xl font-bold text-brand-navy mb-3" data-id="element-490">
          Welcome back
        </h1>
        <p className="text-brand-muted" data-id="element-491">
          Sign in to manage your account, view your balance, and make a payment.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8" data-id="element-492">
        {/* Email */}
        <label htmlFor="email" className="block text-sm font-semibold text-brand-dark mb-1.5" data-id="element-493">
          Email
        </label>
        <div className="relative mb-4" data-id="element-494">
          <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none" data-id="element-495" />
          <input id="email" type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} className="w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-lg text-brand-dark placeholder:text-gray-400 focus:border-brand-navy focus:outline-none transition-colors" required data-id="element-496" />
        </div>

        {/* Password */}
        <label htmlFor="password" className="block text-sm font-semibold text-brand-dark mb-1.5" data-id="element-497">
          Password
        </label>
        <div className="relative" data-id="element-498">
          <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none" data-id="element-499" />
          <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="Your password" value={password} onChange={e => setPassword(e.target.value)} className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-lg text-brand-dark placeholder:text-gray-400 focus:border-brand-navy focus:outline-none transition-colors" required data-id="element-500" />
          <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-navy transition-colors p-1" aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-501">
            {showPassword ? <EyeOff size={18} data-id="element-502" /> : <Eye size={18} data-id="element-503" />}
          </button>
        </div>

        {/* First time toggle — prototype helper to demo verification flow */}
        <label className="mt-4 flex items-start gap-2 text-sm text-brand-muted cursor-pointer select-none" data-id="element-504">
          <input type="checkbox" checked={isFirstTime} onChange={e => setIsFirstTime(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-navy focus:ring-brand-navy" data-id="element-505" />
          <span data-id="element-506">Signing in for the first time</span>
        </label>

        <button type="submit" disabled={!canSubmit} className="w-full mt-6 bg-brand-red hover:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3.5 rounded-lg font-bold text-lg transition-colors shadow-sm" data-id="element-507">
          Log In
        </button>

        <div className="mt-4 text-center" data-id="element-508">
          <a href="#" className="text-sm text-brand-navy hover:text-brand-red font-medium transition-colors" data-id="element-509">
            Forgot password?
          </a>
        </div>

        <div className="my-6 flex items-center gap-3" data-id="element-510">
          <div className="flex-1 h-px bg-gray-200" data-id="element-511"></div>
          <span className="text-xs font-medium text-brand-muted uppercase tracking-wider" data-id="element-512">
            or
          </span>
          <div className="flex-1 h-px bg-gray-200" data-id="element-513"></div>
        </div>

        <div className="text-center" data-id="element-514">
          <a href="#" className="text-sm font-semibold text-brand-navy hover:text-brand-red transition-colors" data-id="element-515">
            New here? Get started
          </a>
        </div>
      </form>

      <p className="text-center text-sm text-brand-muted mt-6" data-id="element-516">
        Need help signing in?{' '}
        <a href="tel:888-765-3854" className="text-brand-navy font-semibold hover:text-brand-red transition-colors" data-id="element-517">
          Call 888-765-3854
        </a>
      </p>
    </motion.div>;
}